/**
 * Hero.jsx - Main landing section of Curis Path website
 * 
 * This component displays:
 * - Accreditation badge (NABH & JCI)
 * - Main headline and value proposition
 * - Call-to-action buttons
 * - Key statistics (doctors, hospitals)
 * 
 * @author Curis Path Development Team
 */

import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';

const Hero = () => {
  /**
   * Smoothly scrolls to the enquiry form section
   * Offset accounts for fixed header height
   */
  const scrollToEnquiry = () => {
    const element = document.getElementById('enquiry');
    if (element) {
      const headerOffset = 80; // Height of fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  /**
   * Scrolls to the specialties section
   */
  const scrollToSpecialties = () => {
    const element = document.getElementById('specialties');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero-section">
      <div className="hero-content">
        
        {/* Accreditation Badge */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 mb-6" 
          style={{
            background: 'rgba(255, 255, 255, 0.4)',
            borderRadius: '2rem',
            backdropFilter: 'blur(4px)'
          }}
        >
          <CheckCircle size={16} style={{ color: 'var(--text-primary)' }} />
          <span 
            className="font-mono" 
            style={{ 
              fontSize: '0.75rem', 
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              color: 'var(--text-primary)'
            }}
          >
            NABH & JCI Accredited Hospitals
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="heading-hero mb-6">
          World-Class Medical Care in India at Affordable Costs
        </h1>

        {/* Value Proposition - Lists all 4 specialties */}
        <p 
          className="body-large mb-8 mx-auto" 
          style={{ maxWidth: '700px', color: 'var(--text-secondary)' }}
        >
          Experience premium healthcare with internationally trained doctors, 
          state-of-the-art facilities, and personalized care. Save up to 70% on 
          Orthopedic, Oncology, Dental, and Cosmetic Surgery procedures.
        </p>

        {/* Call-to-Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {/* Primary CTA - Leads to enquiry form */}
          <button 
            onClick={scrollToEnquiry} 
            className="btn-primary flex items-center gap-2"
          >
            Start Your Journey
            <ArrowRight size={18} />
          </button>
          
          {/* Secondary CTA - Shows specialties */}
          <button 
            onClick={scrollToSpecialties} 
            className="btn-secondary"
          >
            Explore Treatments
          </button>
        </div>

        {/* Statistics Section */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-center">
          {/* Doctor Count */}
          <div>
            <div className="heading-2" style={{ color: 'var(--text-primary)' }}>
              1,000+
            </div>
            <div className="body-small" style={{ color: 'var(--text-muted)' }}>
              Expert Doctors
            </div>
          </div>
          
          {/* Divider */}
          <div style={{ width: '1px', height: '40px', background: 'var(--border-light)' }} />
          
          {/* Hospital Count */}
          <div>
            <div className="heading-2" style={{ color: 'var(--text-primary)' }}>
              50+
            </div>
            <div className="body-small" style={{ color: 'var(--text-muted)' }}>
              Partner Hospitals
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
